/**
 * The shared front half of notification intake (desktop slice 4, R4).
 *
 * WHAT THIS IS. Every surface receives the same opaque ciphertext and runs the same first steps
 * on it: **dedupe by msgId → decrypt → parse → classify by payload type.** Those steps were
 * written three times (`apps/cli`'s `tail-pipeline.ts`, `apps/extension`'s
 * `background/processing.ts`, `apps/web`'s `services/notifications.ts`). This is their home.
 *
 * WHAT THIS DELIBERATELY IS NOT. Everything *after* classification is host policy and stays
 * with the host: which output mode to use, whether to fire an OS notification, when to write a
 * sync cursor, how to filter. Those differ per surface by design, and a shared module that
 * decided them would be one every host has to fight.
 *
 * ── TWO DEPARTURES FROM R4-c's SKETCH, both from reading the adopter rather than the spec ──
 *
 * **1. No `Notifier`, and no `storage`/`analytics` in the deps.** R4-c sketches
 * `IntakeDeps { decrypt, storage, analytics, notifier, now }`. That shape was drafted while
 * *web* was the in-plan adopter; R4-a then overruled that and made `apps/cli` the adopter. The
 * CLI has **no `StorageAdapter`** (it persists to `cli.json`) and **no analytics client at all**
 * — M8b forbids one. Two of five fields are unsatisfiable by the only consumer.
 *
 * Rather than hand it no-op stubs — which is how an interface stops describing its callers —
 * classification RETURNS AN OUTCOME and the host acts on it. A returned value is also easier to
 * test than an injected sink, and it lets each surface keep its own display without this module
 * knowing any of them exist. `Notifier` lands if and when a host actually needs intake to push
 * rather than report; the CLI does not.
 *
 * **2. Dedupe is exposed separately from classification, not fused into one call.** The obvious
 * shape is `accept(message) -> outcome` doing both. It would change behaviour: the CLI dedupes
 * in `ingest`, then — if a reply prompt holds stdin — BUFFERS the envelope and defers decryption
 * to `flushPending`. Fusing the two would decrypt at ingest time instead, moving when a decrypt
 * error reaches stderr relative to the buffering. So the halves stay separable and the host
 * keeps its own ordering.
 *
 * SECURITY: an outcome carries decrypted payload fields. Never log one — no `title`, no `body`,
 * no clipboard `content`. See `.claude/rules/security.md`.
 */

import { isNotificationPayload, type NotificationPayload } from "@reflecto/core";

/** Envelope shape this module needs — structurally satisfied by `@reflecto/core`'s `SyncMessage`. */
export interface IntakeEnvelope {
  seqId: number;
  msgId: string;
  /** Base64 of the `[0x01 | nonce | ciphertext]` wire blob. */
  payload: string;
}

export type IntakeOutcome =
  | {
      kind: "notification";
      msgId: string;
      seqId: number;
      payload: NotificationPayload;
      /**
       * Duplicated out of `payload` on purpose (R4-d-1). A surface discriminating First Light —
       * `dev.reflecto.firstlight` — should not have to reach into the payload to do it, and
       * intake itself deliberately does NOT call `splitSeenBatch`: "first notification seen"
       * means the user actually saw it, which only the render layer knows.
       */
      appPackage: string;
    }
  | {
      kind: "other_type";
      msgId: string;
      seqId: number;
      /**
       * The payload's own `type`, reported rather than judged.
       *
       * WHICH FAMILIES COUNT AS "SOMEONE ELSE'S TRAFFIC" IS A HOST FACT, not a shared one, and
       * an earlier draft of this module got it wrong by hardcoding a seven-family set. The
       * extension ROUTES sms payloads; `reflecto tail` does not, and reports them as malformed.
       * A shared set would have silently converted the CLI's existing stderr line into a silent
       * drop — a behaviour change smuggled inside a move.
       */
      type: string;
    }
  | {
      kind: "failed";
      msgId: string;
      seqId: number;
      reason: "decrypt" | "not_json" | "invalid_shape";
      /** Safe to surface: an error message or a fixed string. Never payload content. */
      detail: string;
    };

export interface ClassifyDeps {
  /**
   * Decrypt a base64 wire blob to plaintext bytes, or throw.
   *
   * The host owns this because it owns the keys — `@reflecto/core`'s `decryptMessage` needs a
   * peer public key and a secret key that only the host has bound.
   */
  decrypt(payloadBase64: string): Uint8Array;
}

/**
 * The outcome of the decrypt→parse half alone, for a host that does its OWN family dispatch and
 * therefore needs the parsed value even when it is not a notification.
 *
 * `value` is `unknown` deliberately. Narrowing it here would mean deciding which families exist,
 * which is the host fact `IntakeOutcome`'s `other_type` comment already refuses to decide.
 */
export type DecryptParseOutcome =
  | { kind: "parsed"; value: unknown }
  | {
      kind: "failed";
      reason: "decrypt" | "not_json";
      /** Safe to surface: an error message or a fixed string. Never payload content. */
      detail: string;
    };

/**
 * Decrypt a wire blob and JSON-parse it. Never throws.
 *
 * SEPARATE FROM `classifyEnvelope` because `apps/web` needs exactly this and nothing more: it
 * dispatches `ping` / `clipboard` / `file_ctrl` itself and needs the PARSED OBJECT for each,
 * which `classifyEnvelope` discards (its `other_type` outcome reports only the type string). It
 * held its own copy of these ten lines until slice 11h; now there is one.
 *
 * `reason` is also what a host should log instead of the thrown error's own fields.
 * `JSON.parse` here runs on DECRYPTED bytes, and V8 quotes the offending fragment into its
 * `SyntaxError` message — that fragment is notification plaintext. The two-value `reason`
 * separates the failures worth telling apart (decrypt vs parse) while touching no content.
 */
export function decryptAndParse(deps: ClassifyDeps, payloadBase64: string): DecryptParseOutcome {
  let plaintext: Uint8Array;
  try {
    plaintext = deps.decrypt(payloadBase64);
  } catch (err) {
    return {
      kind: "failed",
      reason: "decrypt",
      detail: err instanceof Error ? err.message : String(err),
    };
  }

  try {
    return { kind: "parsed", value: JSON.parse(new TextDecoder().decode(plaintext)) };
  } catch {
    return { kind: "failed", reason: "not_json", detail: "decrypted bytes are not valid JSON" };
  }
}

/**
 * Decrypt → parse → classify one envelope. Stateless; dedupe is the caller's (see the header).
 *
 * Never throws: every failure becomes a `failed` outcome, so a malformed message from one
 * sender cannot take down a caller's read loop.
 */
export function classifyEnvelope(deps: ClassifyDeps, envelope: IntakeEnvelope): IntakeOutcome {
  const { msgId, seqId } = envelope;

  const read = decryptAndParse(deps, envelope.payload);
  if (read.kind === "failed") {
    return { kind: "failed", msgId, seqId, reason: read.reason, detail: read.detail };
  }
  const parsed = read.value;

  // Anything that is not a notification is reported BY TYPE; the host decides whether that is a
  // silent drop (its own feature, handled elsewhere) or an error (a family it does not know).
  const type = (parsed as { type?: unknown } | null)?.type;
  if (typeof type === "string" && type !== "notification") {
    return { kind: "other_type", msgId, seqId, type };
  }

  // AFTER the JSON parse and the family check: an `as NotificationPayload` cast on unverified
  // data leaks `undefined` fields into renderers and, downstream, into OS notifications.
  if (!isNotificationPayload(parsed)) {
    return {
      kind: "failed",
      msgId,
      seqId,
      reason: "invalid_shape",
      detail: "decrypted JSON missing required NotificationPayload fields",
    };
  }

  return { kind: "notification", msgId, seqId, payload: parsed, appPackage: parsed.appPackage };
}

export interface SeenTracker {
  /** Records `msgId`; returns false if it was already present (i.e. this is a duplicate). */
  markSeen(msgId: string): boolean;
  has(msgId: string): boolean;
  size(): number;
}

/** Default matches the server's queue cap, so a full replay cannot evict its own head. */
const SEEN_CAP_DEFAULT = 200;

/**
 * Bounded msgId dedupe, insertion-ordered, oldest evicted at the cap.
 *
 * Shared because every surface needs the same guarantee for the same reason: live SSE, a
 * `/v1/sync` replay and a backlog fetch can all deliver the same message, and a user must not
 * see it three times.
 */
export function createSeenTracker(cap: number = SEEN_CAP_DEFAULT): SeenTracker {
  const seen = new Set<string>();
  return {
    markSeen(msgId: string): boolean {
      if (seen.has(msgId)) return false;
      if (seen.size >= cap) {
        const first = seen.values().next().value;
        if (first !== undefined) seen.delete(first);
      }
      seen.add(msgId);
      return true;
    },
    has(msgId: string): boolean {
      return seen.has(msgId);
    },
    size(): number {
      return seen.size;
    },
  };
}
